import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Bot, ChevronDown, ExternalLink, Handshake, Store } from "lucide-react";
import { tryNowProducts } from "@/lib/constants";
import { cn } from "@/lib/utils";

const iconMap = {
  Handshake,
  Bot,
  Store,
} as const;

interface StartFreeTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  controlsId: string;
  expanded: boolean;
}

export const StartFreeTrigger = forwardRef<
  HTMLButtonElement,
  StartFreeTriggerProps
>(({ className, controlsId, expanded, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-controls={controlsId}
    aria-expanded={expanded}
    className={cn(
      "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-retro-line bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_2px_0_#173c17] transition-[transform,background-color,box-shadow] duration-150 hover:-translate-y-px hover:bg-primary-700 hover:shadow-[0_3px_0_#173c17] active:translate-y-px active:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 motion-reduce:transform-none motion-reduce:transition-none",
      className,
    )}
    {...props}
  >
    <span>Start for Free</span>
    <ChevronDown
      aria-hidden="true"
      className={cn(
        "size-4 transition-transform duration-200 motion-reduce:transition-none",
        expanded && "rotate-180",
      )}
    />
  </button>
));

StartFreeTrigger.displayName = "StartFreeTrigger";

interface StartFreeLauncherContentProps {
  className?: string;
  onNavigate?: () => void;
}

export function StartFreeLauncherContent({
  className,
  onNavigate,
}: StartFreeLauncherContentProps) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-line bg-panel",
        className,
      )}
    >
      <div className="border-b border-primary-700 bg-primary-600 px-5 py-4">
        <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-white/80">
          Buddify apps
        </p>
        <p className="mt-1 font-heading text-lg font-bold tracking-tight text-white">
          Choose where to start
        </p>
        <p className="mt-1 text-xs leading-relaxed text-white/80">
          Choose the Buddify product that fits your next move.
        </p>
      </div>

      <div className="space-y-0.5 p-2">
        {tryNowProducts.map((product) => {
          const Icon = iconMap[product.icon];
          const isAi = product.color === "secondary";
          const iconClasses = isAi
            ? "bg-brand-violet-soft text-brand-violet"
            : "bg-brand-green-soft text-brand-green";

          if ("links" in product) {
            return (
              <div
                key={product.name}
                className="grid grid-cols-[2.25rem_1fr] gap-x-3 rounded-lg border border-transparent p-2.5"
              >
                <div
                  className={cn(
                    "flex size-9 items-center justify-center rounded-lg",
                    iconClasses,
                  )}
                >
                  <Icon aria-hidden="true" className="size-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">
                    {product.name}
                  </p>
                  <p className="mt-0.5 text-xs text-muted">
                    Sell products or shop deals
                  </p>
                </div>
                <div className="col-start-2 mt-2 grid grid-cols-2 gap-2">
                  {product.links.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onNavigate}
                      className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg border border-brand-green-line bg-panel px-2 text-center text-[11px] font-semibold text-brand-green transition-colors hover:bg-brand-green-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-1"
                    >
                      {link.label}
                      <ExternalLink aria-hidden="true" className="size-3" />
                    </a>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <a
              key={product.name}
              href={product.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavigate}
              className="group grid grid-cols-[2.25rem_1fr_auto] items-center gap-3 rounded-lg border border-transparent p-2.5 transition-colors hover:border-brand-green-line hover:bg-brand-green-soft/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-inset"
            >
              <div
                className={cn(
                  "flex size-9 items-center justify-center rounded-lg",
                  iconClasses,
                )}
              >
                <Icon aria-hidden="true" className="size-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">
                  {product.name}
                </p>
                <p className="mt-0.5 text-xs text-muted">
                  {product.tagline}
                </p>
              </div>
              <ExternalLink
                aria-hidden="true"
                className={cn(
                  "size-3.5 transition-colors",
                  isAi
                    ? "text-brand-violet group-hover:text-brand-violet"
                    : "text-brand-green group-hover:text-brand-green",
                )}
              />
            </a>
          );
        })}
      </div>
    </div>
  );
}
